import { CheckIcon, ChevronsUpDown } from 'lucide-react'
import * as React from 'react'
import * as RPNInput from 'react-phone-number-input'
import flags from 'react-phone-number-input/flags'
import { useTranslations } from 'use-intl'

import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'

const countryPlaceholders: Partial<Record<RPNInput.Country, string>> = {
  FR: '06 01 02 03 04',
  US: '(555) 123-4567',
  GB: '07700 900123',
  DE: '0151 12345678',
  ES: '612 34 56 78',
  IT: '312 345 6789',
  CA: '(555) 123-4567',
  AU: '0412 345 678',
  JP: '090-1234-5678',
  BR: '(11) 91234-5678',
  IN: '81234 56789',
  CN: '138 0013 8000',
  KR: '010-1234-5678',
  MX: '222 123 4567',
  AR: '11 1234-5678',
  RU: '8 (912) 345-67-89',
  TR: '0532 123 45 67',
  SA: '050 123 4567',
  EG: '010 1234 5678',
  ZA: '082 123 4567',
  NG: '0802 123 4567',
  KE: '0712 123456',
  MA: '0661-234567',
  TN: '20 123 456',
  DZ: '0551 23 45 67',
  CH: '078 123 45 67',
  AT: '0664 123456',
  BE: '0470 12 34 56',
  NL: '06 12345678',
  NO: '406 12 345',
  SE: '070-123 45 67',
  DK: '20 12 34 56',
  FI: '050 123 4567',
  PT: '912 345 678',
  IE: '085 123 4567',
  LU: '628 123 456',
  PL: '512 345 678',
  CZ: '601 123 456',
  SK: '0901 123 456',
  HU: '(20) 123 4567',
  RO: '0712 345 678',
  BG: '087 123 4567',
  HR: '098 123 4567',
  SI: '031 123 456',
  EE: '5123 4567',
  LV: '2123 4567',
  LT: '(8-612) 12345',
  IS: '611 1234',
  MT: '9696 1234',
  CY: '96 123456',
  GR: '694 123 4567',
}

const getPlaceholderForCountry = (
  country: RPNInput.Country | undefined,
  defaultPlaceholder: string,
): string => {
  if (!country) return defaultPlaceholder

  return countryPlaceholders[country] || defaultPlaceholder
}

type PhoneInputProps = Omit<
  React.ComponentProps<'input'>,
  'onChange' | 'value' | 'ref'
> &
  Omit<RPNInput.Props<typeof RPNInput.default>, 'onChange'> & {
    onChange?: (value: RPNInput.Value) => void
  }

const PhoneInput: React.ForwardRefExoticComponent<PhoneInputProps> =
  React.forwardRef<React.ElementRef<typeof RPNInput.default>, PhoneInputProps>(
    ({ className, onChange, value, ...props }, ref) => {
      const t = useTranslations('common')
      const [country, setCountry] = React.useState<
        RPNInput.Country | undefined
      >(props.defaultCountry)

      const placeholder = React.useMemo(() => {
        const defaultPlaceholder = t('phoneInput.placeholder')
        return getPlaceholderForCountry(country, defaultPlaceholder)
      }, [country, t])

      return (
        <RPNInput.default
          ref={ref}
          className={cn('flex', className)}
          flagComponent={FlagComponent}
          countrySelectComponent={CountrySelect}
          inputComponent={InputComponent}
          smartCaret={false}
          value={value || undefined}
          placeholder={placeholder}
          onCountryChange={setCountry}
          /**
           * Handles the onChange event.
           *
           * react-phone-number-input might trigger the onChange event as undefined
           * when a valid phone number is not entered. To prevent this,
           * the value is coerced to an empty string.
           *
           * @param {E164Number | undefined} value - The entered value
           */
          onChange={(value) => onChange?.(value || ('' as RPNInput.Value))}
          {...props}
        />
      )
    },
  )
PhoneInput.displayName = 'PhoneInput'

const InputComponent = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<'input'>
>(({ className, ...props }, ref) => (
  <Input
    className={cn('rounded-e-lg rounded-s-none', className)}
    {...props}
    ref={ref}
  />
))
InputComponent.displayName = 'InputComponent'

type CountryEntry = { label: string; value: RPNInput.Country | undefined }

type CountrySelectProps = {
  disabled?: boolean
  value: RPNInput.Country
  options: CountryEntry[]
  onChange: (country: RPNInput.Country) => void
}

const CountrySelect = ({
  disabled,
  value: selectedCountry,
  options: countryList,
  onChange,
}: CountrySelectProps) => {
  const scrollAreaRef = React.useRef<HTMLDivElement>(null)
  const [searchValue, setSearchValue] = React.useState('')
  const [isOpen, setIsOpen] = React.useState(false)

  const handleSearchChange = React.useCallback(
    (value: string) => {
      setSearchValue(value)
      // Use requestAnimationFrame for better React 19 compatibility
      requestAnimationFrame(() => {
        if (scrollAreaRef.current) {
          const viewportElement = scrollAreaRef.current.querySelector(
            '[data-radix-scroll-area-viewport]',
          )
          if (viewportElement) {
            viewportElement.scrollTop = 0
          }
        }
      })
    },
    [scrollAreaRef],
  )

  return (
    <Popover
      open={isOpen}
      modal
      onOpenChange={(open) => {
        setIsOpen(open)
        open && setSearchValue('')
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="flex gap-1 rounded-e-none rounded-s-lg border-r-0 px-3 py-[17px] focus:z-10"
          disabled={disabled}
        >
          <FlagComponent
            country={selectedCountry}
            countryName={selectedCountry}
          />
          <ChevronsUpDown
            className={cn(
              '-mr-2 size-4 opacity-50',
              disabled ? 'hidden' : 'opacity-100',
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0">
        <Command>
          <CommandInput
            value={searchValue}
            onValueChange={handleSearchChange}
            placeholder="Search country..."
          />
          <CommandList>
            <ScrollArea ref={scrollAreaRef} className="h-72">
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup>
                {countryList.map(({ value, label }) =>
                  value ? (
                    <CountrySelectOption
                      key={value}
                      country={value}
                      countryName={label}
                      selectedCountry={selectedCountry}
                      onChange={onChange}
                      onSelectComplete={() => setIsOpen(false)}
                    />
                  ) : null,
                )}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

interface CountrySelectOptionProps extends RPNInput.FlagProps {
  selectedCountry: RPNInput.Country
  onChange: (country: RPNInput.Country) => void
  onSelectComplete: () => void
}

const CountrySelectOption = ({
  country,
  countryName,
  selectedCountry,
  onChange,
  onSelectComplete,
}: CountrySelectOptionProps) => {
  const handleSelect = () => {
    onChange(country)
    onSelectComplete()
  }

  return (
    <CommandItem className="gap-2" onSelect={handleSelect}>
      <FlagComponent country={country} countryName={countryName} />
      <span className="flex-1 text-sm">{countryName}</span>
      <span className="text-sm text-foreground/50">{`+${RPNInput.getCountryCallingCode(country)}`}</span>
      <CheckIcon
        className={`ml-auto size-4 ${country === selectedCountry ? 'opacity-100' : 'opacity-0'}`}
      />
    </CommandItem>
  )
}

const FlagComponent = ({ country, countryName }: RPNInput.FlagProps) => {
  const Flag = flags[country]

  return (
    <span className="flex h-4 w-6 overflow-hidden rounded-sm bg-foreground/20 [&_svg:not([class*='size-'])]:size-full">
      {Flag && <Flag title={countryName} />}
    </span>
  )
}

export { PhoneInput }
